import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { Target, Check, Circle, ChevronRight, X, Ticket } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';
import { Gender } from '@/gql/graphql';
import { RACE_BETTORS } from '@/graphql/queries';
import { UserAvatar } from '@/components/ui/UserAvatar';

const MAX_AVATARS = 3;

interface BettingActivityCardProps {
    raceId: string;
}

export function BettingActivityCard({ raceId }: BettingActivityCardProps) {
    const { t } = useTranslation('races');
    const { t: tb } = useTranslation('bets');
    const { user: currentUser } = useAuth();
    const [showModal, setShowModal] = useState(false);

    const { data, loading } = useQuery(RACE_BETTORS, {
        variables: { raceId, pagination: { page: 1, limit: 50 } },
    });

    if (loading) return null;

    const items = data?.raceBettors?.items ?? [];
    const totalCount = data?.raceBettors?.totalCount ?? 0;

    // Current user's betting status
    const myEntry = items.find(item => item.user?.id === currentUser?.id);
    const myGenders = new Set(myEntry?.bet?.genders ?? []);

    // Other bettors (excluding current user)
    const otherBettors = items.filter(item => item.user?.id !== currentUser?.id);
    const displayedBettors = otherBettors.slice(0, MAX_AVATARS);
    const extraCount = totalCount - displayedBettors.length - (myEntry ? 1 : 0);

    if (totalCount === 0 && !myEntry) {
        return (
            <div className="rounded-2xl bg-slate-50 p-4 border border-slate-100 text-center">
                <Target size={24} className="mx-auto mb-2 text-slate-300" />
                <p className="text-sm font-medium text-slate-500">{t('details.bettingActivity.noBetsYet')}</p>
                <p className="text-xs text-slate-400 mt-1">{t('details.bettingActivity.beFirst')}</p>
            </div>
        );
    }

    return (
        <>
            <button
                type="button"
                onClick={() => setShowModal(true)}
                className="w-full text-left rounded-2xl bg-emerald-50/50 p-4 border border-emerald-100 hover:border-emerald-200 transition-colors"
            >
                {/* Avatars row */}
                <div className="flex items-center gap-3">
                    <div className="flex -space-x-2">
                        {displayedBettors.map(item => (
                            <div key={item.user?.id} className="relative">
                                <UserAvatar
                                    src={item.user?.pictureUrl}
                                    alt={item.user?.username ?? ''}
                                    size="xs"
                                    className="border-2 border-emerald-100"
                                />
                                <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-emerald-500 flex items-center justify-center border-[1.5px] border-white">
                                    <Check size={7} className="text-white" />
                                </div>
                            </div>
                        ))}
                        {extraCount > 0 && (
                            <div className="w-8 h-8 rounded-full bg-emerald-100 border-2 border-emerald-50 flex items-center justify-center flex-shrink-0">
                                <span className="text-[10px] font-bold text-emerald-700">+{extraCount}</span>
                            </div>
                        )}
                    </div>

                    <div className="flex-1 min-w-0">
                        {totalCount > 0 && (
                            <p className="text-sm font-bold text-emerald-800">
                                {t('details.bettingActivity.count', { count: totalCount })}
                            </p>
                        )}
                        {displayedBettors.length > 0 && (
                            <p className="text-xs text-emerald-600 truncate">
                                {displayedBettors.map(b => b.user?.username).join(', ')}
                                {extraCount > 0 && '...'}
                            </p>
                        )}
                    </div>

                    <ChevronRight size={14} className="text-emerald-400 flex-shrink-0" />
                </div>

                {/* Current user status */}
                {currentUser && (
                    <div className="mt-3 pt-3 border-t border-emerald-100 flex items-center gap-3">
                        <span className="text-xs font-bold text-emerald-700">{t('details.bettingActivity.you')} :</span>
                        <div className="flex items-center gap-2">
                            <GenderStatus label={tb('gender.MALE')} done={myGenders.has(Gender.Male)} />
                            <GenderStatus label={tb('gender.FEMALE')} done={myGenders.has(Gender.Female)} />
                        </div>
                    </div>
                )}
            </button>

            {showModal && (
                <BettorsModal
                    items={items}
                    totalCount={totalCount}
                    onClose={() => setShowModal(false)}
                />
            )}
        </>
    );
}

function GenderStatus({ label, done }: { label: string; done: boolean }) {
    return (
        <span className={cn(
            "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold",
            done ? "bg-emerald-100 text-emerald-700" : "bg-white text-slate-400"
        )}>
            {done ? <Check size={10} /> : <Circle size={10} />}
            {label}
        </span>
    );
}

interface BettorEntry {
    user?: { id?: string | null; username?: string | null; pictureUrl?: string | null } | null;
    bet?: { genders?: (Gender | null)[] | null } | null;
}

function BettorsModal({ items, totalCount, onClose }: {
    items: BettorEntry[];
    totalCount: number;
    onClose: () => void;
}) {
    const { t } = useTranslation('races');
    const { t: tb } = useTranslation('bets');

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, []);

    return createPortal(
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center">
            <div
                className="absolute inset-0 bg-slate-900/20 backdrop-blur-[2px] animate-in fade-in duration-200"
                onClick={onClose}
            />

            <div className="bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl w-full max-w-sm relative z-10 animate-in slide-in-from-bottom-4 sm:zoom-in-95 duration-200 border border-slate-100 max-h-[80vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center gap-3 p-5 pb-4 border-b border-slate-100 flex-shrink-0">
                    <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                        <Ticket size={16} className="text-emerald-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="font-bold text-slate-900">
                            {t('details.bettingActivity.modalTitle')}
                        </p>
                        <p className="text-xs text-slate-400">
                            {t('details.bettingActivity.count', { count: totalCount })}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* List */}
                <div className="overflow-y-auto p-3 space-y-1">
                    {items.map(item => {
                        const genders = item.bet?.genders ?? [];
                        return (
                            <div key={item.user?.id} className="flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-slate-50">
                                <UserAvatar
                                    src={item.user?.pictureUrl}
                                    alt={item.user?.username ?? ''}
                                    size="xs"
                                />
                                <span className="text-sm font-medium text-slate-800 flex-1 truncate">
                                    {item.user?.username}
                                </span>
                                <div className="flex gap-1">
                                    {genders.includes(Gender.Male) && (
                                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600">
                                            {tb('gender.MALE')}
                                        </span>
                                    )}
                                    {genders.includes(Gender.Female) && (
                                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-pink-50 text-pink-600">
                                            {tb('gender.FEMALE')}
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>,
        document.body,
    );
}
